import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, MapPin, Upload, X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
// import ImageUpload from '../components/common/ImageUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CreateServicePage() {
  const navigate = useNavigate();
  const { isAuthenticated, api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category_id: '',
    credits_per_hour: 1,
    duration_min: 60,
    location: 'Ottignies-Louvain-la-Neuve',
    image_url: '',
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCategories();
  }, [isAuthenticated, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.category_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/services', form);
      toast.success('Service créé avec succès !');
      navigate(`/services/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm({
            ...form,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          toast.success('Position récupérée');
        },
        (error) => {
          toast.error('Impossible de récupérer votre position');
        }
      );
    }
  };

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-2xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="text-2xl">Proposer un service</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Titre du service *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex: Cours de piano pour débutants"
                  required
                  className="rounded-xl"
                  data-testid="service-title-input"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie *</Label>
                <Select
                  value={form.category_id}
                  onValueChange={(value) => setForm({ ...form, category_id: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="category-select">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Décrivez votre service en détail..."
                  required
                  className="rounded-xl min-h-[150px]"
                  data-testid="service-description-input"
                />
              </div>

              {/* Credits & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credits">Crédits par heure</Label>
                  <Input
                    id="credits"
                    type="number"
                    min="1"
                    value={form.credits_per_hour}
                    onChange={(e) => setForm({ ...form, credits_per_hour: parseInt(e.target.value) })}
                    className="rounded-xl"
                  />
                  <p className="text-xs text-muted-foreground">
                    Par défaut, 1 heure = 1 crédit
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée minimum (min)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="15"
                    step="15"
                    value={form.duration_min}
                    onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Localisation *</Label>
                <div className="flex gap-2">
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ottignies-Louvain-la-Neuve"
                    required
                    className="rounded-xl flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl"
                    onClick={handleGetLocation}
                  >
                    <MapPin className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Image Upload - Temporarily disabled */}
              <div className="space-y-2">
                <label className="block text-sm font-medium">Image du service (optionnel)</label>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="input-field"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="submit-service-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Création en cours...
                  </>
                ) : (
                  'Publier le service'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
